import { Component, Inject, Prop, Watch } from 'vue-property-decorator';
import Vue from 'vue';
import { CC_CONTAINER_TYPES } from '../infrastructure/ioc/cc-container-types';
import { Utils } from '../infrastructure/utils/helpers';
import CcServiceInterface from '../service/cc-service-interface';
import CcStore from '@/main/webapp/resources/src/vue/domain/store/cc-store';
import CcUser from '../domain/user/cc-user';
import CcBannerArea from '@/main/webapp/resources/src/vue/domain/banner/cc-banner-area';
import CcBannerAreaCode from '@/main/webapp/resources/src/vue/domain/banner/cc-banner-area-code';
import 'vue-multiselect/dist/vue-multiselect.min.css';
import CcOrder from '@/main/webapp/resources/src/vue/domain/order/cc-order';
import Swal from 'sweetalert2';
import CcCustomer from '@/main/webapp/resources/src/vue/domain/user/cc-customer';
import CcProductStock from '@/main/webapp/resources/src/vue/domain/productstock/cc-productstock';
import CcOrdertype from '@/main/webapp/resources/src/vue/domain/order/cc-ordertype';
import CcProductTag from '@/main/webapp/resources/src/vue/domain/product/cc-product-tag';

@Component({
	components: {
		CcHeader: Utils.getCustomComponent('cc-header'),
		CcFooter: Utils.getCustomComponent('cc-footer'),
		CcApplicationNavigationMenu: Utils.getCustomComponent('cc-application-navigation-menu'),
		CcCustomerShipping: () => import(/* webpackChunkName: 'cc-customer-shipping' */ '@/main/webapp/resources/src/vue/components/cc-page-customer-shipping/cc-customer-shipping-form/cc-customer-shipping-form.vue'),
		CcOrderOptionsConfiguration: () => import(/* webpackChunkName: 'cc-order-options-configuration' */ '@/main/webapp/resources/src/vue/components/cc-order-options-configuration/cc-order-options-configuration.vue'),
		UiModal: () => import(/* webpackChunkName: 'ui-modal' */ '@/main/webapp/resources/src/vue/components/ui-modal/ui-modal.vue')
	}
})
export default class CcApplicationComponent extends Vue {
	@Inject(CC_CONTAINER_TYPES.CcServiceInterface)
	private service!: CcServiceInterface;
	@Prop({ type: Boolean, default: false })
	private accountEnabled!: boolean;
	@Prop({ type: Boolean, default: false })
	private passwordEnabled!: boolean;
	private lang: string = '';
	private isNavigationMenuOpen: boolean = false;
	private working = false;
	private workingUnavailableDates = false;
	private showCustomShipping: boolean = false;
	private showOrderOptionsModalOnStartup: boolean = null;
	static readonly SHOW_ON_STARTUP: string = 'cc-order-options-configuration.show_on_startup';

	beforeMount () {
		this.lang = Utils.getLanguageCode();
		this.loadStore();
		this.loadBannerProducts();
		this.loadBannerHome();
		this.loadBannerHomePrivate();
		if (Utils.isFilterTagsEnabled()) {
			this.loadTags();
		}
		this.showOrderOptionsModalOnStartup = Utils.localStorageGetItem(CcApplicationComponent.SHOW_ON_STARTUP) !== 'false';
		this.$store.dispatch('setOrderNotes', Utils.localStorageGetItem(Utils.ORDER_NOTES) || '');
	}

	created () {
		this.$root.$on('toggle-application-navigation-menu', this.toggleApplicationNavigationMenu);
		this.$root.$on('logout', this.logout);
		this.$root.$on('saveOrderType', this.saveOrderType);
		this.$root.$on('loadUnavailableDates', this.loadUnavailableDates);
		this.$root.$on('saveCustomerShipping', (params: any) => this.saveCustomerShippingModal(params));
		this.$root.$on('loadOrders', (params: any) => this.loadOrders(params));
		this.$root.$on('changeStoreRequest', (params: any) => this.changeStoreRequest(params));
	}

	get appKey (): number {
		return this.$store.getters.appKey;
	}

	get user (): CcUser {
		return this.$store.getters.user;
	}

	set user (user: CcUser) {
		this.$store.dispatch('setUser', user);
	}

	get readyToStart (): boolean {
		return this.$store.getters.readyToStart;
	}

	get userLogged (): boolean {
		return this.$store.getters.user ? this.$store.getters.user.logged : false;
	}

	get isScrollTop (): boolean {
		return (document.body.scrollTop === 0);
	}

	get currentOrder (): CcOrder {
		return this.$store.getters.order;
	}

	set currentOrder (currentOrder: CcOrder) {
		this.$store.dispatch('setOrder', currentOrder);
	}

	get customerShippingFormData (): any {
		return this.$store.getters.customerShippingForm.shippingData;
	}

	get customerShippingFormErrors (): Array<any> {
		return this.$store.getters.customerShippingForm.errors;
	}

	set customerShippingFormErrors (errors: Array<any>) {
		this.$store.dispatch('setCustomerShippingFormErrors', errors);
	}

	get currentCustomer (): CcCustomer {
		return this.user?.currentCustomer || null;
	}

	get orders (): Array<CcOrder> {
		return this.$store.getters.orders;
	}

	set orders (orders: Array<CcOrder>) {
		this.$store.dispatch('setOrders', orders);
	}

	get ordersPageData (): any {
		return this.$store.getters.ordersPageData;
	}

	set ordersPageData (ordersPageData: any) {
		this.$store.dispatch('setOrdersPageData', ordersPageData);
	}

	get orderType (): CcOrdertype {
		return this.$store.getters.orderType;
	}

	get orderOptionsModalId (): string {
		return Utils.ORDER_OPTIONS_MODAL_ID;
	}

	@Watch('$route.name', { immediate: true })
	private routeNameChange () {
		if (this.$route.name === 'login' && this.user && this.user.logged) {
			this.$router.push({ name: 'home', params: { reload: 'true' } });
		}
	}

	@Watch('currentCustomer')
	private currentCustomerChange (newValue: CcCustomer, oldValue: CcCustomer) {
		if (newValue && oldValue && newValue?.code !== oldValue?.code) {
			this.$router.push({ name: 'home', params: { reset: 'true' } });
		}
	}

	@Watch('showOrderOptionsModalOnStartup')
	private showOrderOptionsModalOnStartupChange () {
		Utils.localStorageSetItem(CcApplicationComponent.SHOW_ON_STARTUP, this.showOrderOptionsModalOnStartup.toString());
	}

	private loadCurrentOrder () {
		const elementToMask: HTMLElement = <HTMLElement>(document.getElementById('page-content'));
		Utils.setWorking(true, elementToMask);
		this.service.getCurrentOrder({})
			.then((orderData: CcOrder) => {
				Utils.setWorking(false, elementToMask);
				// this.setAnaliticsTraker(); checkout??
				this.$store.dispatch('setOrder', orderData);
				if (this.$route.name === 'orders') {
					this.loadOrders({ data: {},
						thenCallback: (response: any) => {
							this.orders = [];
							this.orders = this.orders.concat(response.orders);
							if (response.page) {
								this.ordersPageData = response.page;
							}
						} });
				}
			})
			.catch((error: Error) => {
				Utils.defaultApiErrorHandler(error);
				Utils.setWorking(false, elementToMask);
			});
	}

	private loadOrders (params: any) {
		Utils.setWorking(true);
		this.service.getOrders(params.data).then((response: any) => {
			if (params.thenCallback) {
				params.thenCallback(response);
			}
			Utils.setWorking(false);
		})
			.catch((error: Error) => {
				Utils.setWorking(false);
				Utils.defaultApiErrorHandler(error);
			});
	}

	private loadStore () {
		this.service.getStores({}).then((storesData: Array<CcStore>) => {
			this.$store.dispatch('setStores', storesData);
		}).catch((error: Error) => {
			Utils.defaultApiErrorHandler(error);
		});
	}

	private loadTags () {
		this.service.getTags().then((productTags: Array<CcProductTag>) => {
			this.$store.dispatch('setProductTags', productTags);
		}).catch((error: Error) => {
			Utils.defaultApiErrorHandler(error);
		});
	}

	private loadBannerHome () {
		const bannerAreaCode: CcBannerAreaCode = window.innerWidth < 992 ? CcBannerAreaCode.homepageMobile.name : CcBannerAreaCode.homepage.name;
		this.service.getBannerArea(bannerAreaCode).then((bannerArea: CcBannerArea) => {
			this.$store.dispatch('setBannerAreaHome', bannerArea);
		}).catch((error: Error) => {
			Utils.defaultApiErrorHandler(error);
		});
	}

	private loadBannerHomePrivate () {
		const bannerAreaCode: CcBannerAreaCode = window.innerWidth < 992 ? CcBannerAreaCode.homepageMobilePrivate.name : CcBannerAreaCode.homepagePrivate.name;
		this.service.getBannerArea(bannerAreaCode).then((bannerArea: CcBannerArea) => {
			this.$store.dispatch('setBannerAreaHomePrivate', bannerArea);
		}).catch((error: Error) => {
			Utils.defaultApiErrorHandler(error);
		});
	}

	private loadBannerProducts () {
		this.service.getBannerArea(CcBannerAreaCode.products.name).then((bannerArea: CcBannerArea) => {
			this.$store.dispatch('setBannerAreaProducts', bannerArea);
		}).catch((error: Error) => {
			Utils.defaultApiErrorHandler(error);
		});
	}

	private toggleApplicationNavigationMenu (): void {
		this.isNavigationMenuOpen = !this.isNavigationMenuOpen;
		(this.isNavigationMenuOpen ? Utils.setApplicationScrollable(false) : Utils.setApplicationScrollable(true));
		this.$root.$emit('toggle-application-navigation-menu-expanded-status');
	}

	private logout () {
		this.service.logout().then(() => {
			this.$store.dispatch('setUser', null);
			this.$router.push({ name: 'home', params: { reload: 'true' } });
		});
	}

	private loadUnavailableDates (params: any) {
		if (!this.workingUnavailableDates) {
			this.workingUnavailableDates = true;
			this.service
				.getUnavailableDates(params)
				.then((disabledDates: Array<Date>) => {
					if (params.thenFunction) { params.thenFunction(disabledDates); }
					this.workingUnavailableDates = false;
				})
				.catch((error: Error) => {
					this.working = false;
					Utils.defaultApiErrorHandler(error);
				});
		}
	}

	private acceptCustomerShippingModal () {
		this.saveCustomerShippingModal({ thenCallback: this.closeCustomerShippingModal });
	}

	private closeCustomerShippingModal () {
		this.$root.$emit('ui-modal.close', 'customer-shipping-modal');
	}

	private saveCustomerShippingModal (params: any) {
		const possibleNumber: string = (this.customerShippingFormData.address || '').match(/(\d+)/);
		this.customerShippingFormErrors = [];

		if (!this.customerShippingFormData.validateAddress) {
			this.customerShippingFormErrors = [{ code: 'NotValidateAddress', field: 'address' }];
		} else if (!possibleNumber && !params.skipNumerControll) {
			Swal.fire({
				title: this.$t('cc_components_cc_application_missing_number_title', {}),
				html: this.$t('cc_components_cc_application_missing_number_body', {}),
				type: 'warning',
				showCancelButton: true,
				buttonsStyling: false,
				reverseButtons: true,
				confirmButtonClass: 'ui-button primary round',
				cancelButtonClass: 'ui-button',
				confirmButtonText: this.$t('cc_components_cc_application_missing_number_confirm', {}).toString(),
				cancelButtonText: this.$t('cc_components_cc_application_missing_number_cancel', {}).toString()
			}).then((result) => {
				if (result.value) {
					params.skipNumerControll = true;
					this.saveCustomerShippingModal(params);
				}
			});
		} else if (this.customerShippingFormData.code) {
			this.service.updatesetShippingDestinations(this.customerShippingFormData).then((user: CcUser) => {
				this.user = user;
				if (params.thenCallback) {
					params.thenCallback();
				}
			}).catch((error: any) => {
				if (error.response.data.errors) {
					this.customerShippingFormErrors = error.response.data.errors;
				} else {
					Utils.defaultApiErrorHandler(error);
				}
			});
		} else {
			this.service.createShippingDestinations(this.customerShippingFormData).then((user: CcUser) => {
				this.user = user;
				const customerShipping: CcCustomer = this.user.currentCustomer.shippingDestinations.find((shipping:CcCustomer) => shipping.name === this.customerShippingFormData.name);
				this.customerShippingRouteAlert(customerShipping);
				if (customerShipping?.hasRoute) {
					this.changeShipping(customerShipping);
				}
				if (params.thenCallback) {
					params.thenCallback();
				}
			}).catch((error: any) => {
				if (error.response.data.errors) {
					this.customerShippingFormErrors = error.response.data.errors;
				} else {
					Utils.defaultApiErrorHandler(error);
				}
			});
		}
	}

	private customerShippingRouteAlert (customerShipping: CcCustomer) {
		Swal.fire({
			title: this.$t('cc_components_cc_application_customer_shipping_route_info_title', {}),
			html: customerShipping.hasRoute ? this.$t('cc_components_cc_application_customer_shipping_route_info_body_route', { 0: customerShipping.name }) : this.$t('cc_components_cc_application_customer_shipping_route_info_body_not_route', { 0: customerShipping.name }),
			type: customerShipping.hasRoute ? 'info' : 'warning',
			buttonsStyling: false,
			reverseButtons: true,
			confirmButtonClass: 'ui-button primary round',
			cancelButtonClass: 'ui-button',
			confirmButtonText: this.$t('cc_components_cc_application_customer_shipping_route_info_confirm', {}).toString()
		});
	}

	private changeShipping (customerShipping: CcCustomer) {
		if (customerShipping.routeStore?.code === this.currentOrder.store.code) {
			const shipping: CcCustomer = this.user.currentCustomer.shippingDestinations.find((shippingOption: CcCustomer) => shippingOption.code === customerShipping.code);
			this.saveOrderType({
				type: 'DELIVERY',
				shippingDestinationCode: shipping ? shipping.code : null
			});
		} else {
			this.changeStoreRequest({
				customerShipping: customerShipping,
				defaultShipping: false
			});
		}
	}

	private saveOrderType (params: any) {
		if (!this.working) {
			Utils.setWorking(true);
			this.working = true;
			this.service
				.setOrderType(params)
				.then((order: CcOrder) => {
					this.currentOrder = order;
					if (params.thenFunction) { params.thenFunction(order); }
					this.working = false;
					Utils.setWorking(false);
				})
				.catch((error: Error) => {
					if (params.catchFunction) {
						params.catchFunction(error);
					} else {
						Utils.defaultApiErrorHandler(error);
					}
					Utils.setWorking(false);
					this.working = false;
				});
		}
	}

	private changeStoreRequest (params: any) {
		Swal.fire({
			title: this.$t('cc_components_cc_order_sale_options_change_store_title', {}),
			html: this.$t('cc_components_cc_order_sale_options_change_order_type_body', { 0: params.customerShipping.routeStore.name }),
			type: 'warning',
			showCancelButton: true,
			buttonsStyling: false,
			reverseButtons: true,
			confirmButtonClass: 'ui-button primary round',
			cancelButtonClass: 'ui-button',
			confirmButtonText: this.$t('cc_components_cc_order_sale_options_change_store_confirm', {}).toString(),
			cancelButtonText: this.$t('cc_components_cc_order_sale_options_change_store_cancel', {}).toString()
		}).then((result) => {
			if (result.value) {
				this.saveOrderType({
					type: 'DELIVERY',
					shippingDestinationCode: params.defaultShipping ? null : params.customerShipping.code,
					thenFunction: params.thenFunction
				});
				this.$store.dispatch('setIsStoreOptionsShowed', false);
			} else if (params.catchFunction) {
				params.catchFunction();
			}
		});
	}

	private setAnaliticsTraker () {
		const params: any = {
			event: 'checkout',
			order_number: this.currentOrder.number,
			store: this.currentOrder.store.name,
			type: this.$t(this.currentOrder.type.label || `enum_order_type_${this.currentOrder.type}_label`).toString(),
			products_number: this.currentOrder.total,
			total_amount: this.currentOrder.totalAmount,
			preparation_cost: this.currentOrder.preparationCost ? this.currentOrder.preparationCost.endPrice || this.currentOrder.preparationCost.startPrice : '',
			products: this.currentOrder.items.map((item: CcProductStock) => {
				return {
					item_name: item.product.customerDescription || '',
					item_id: item.product.code,
					price: item.price,
					store: this.currentOrder.store.name,
					quantity: item.quantity
				};
			})
		};
		Utils.setAnaliticsTraker(params);
	}

	private acceptOrderOptionsModal () {
		if (this.showCustomShipping) {
			this.$root.$emit('saveCustomerShipping', { thenCallback: this.showOrderOptionsModal });
		} else {
			document.getElementsByClassName('put-animation-here')[0].classList.add('pulse');
			this.$root.$emit('ui-modal.close', this.orderOptionsModalId);
		}
	}

	private showOrderOptionsModal () {
		this.showCustomShipping = false;
	}

	private showCustomerShippingModal () : void {
		this.$store.dispatch('setCustomerShippingFormData', {});
		this.$store.dispatch('setCustomerShippingFormErrors', []);
		this.showCustomShipping = true;
	}
}
